import React, { useState, useEffect } from 'react';
import axios from '../../../Axiosinstance';
import Translate from '@mui/icons-material/Translate';
import VpnKey from '@mui/icons-material/VpnKey';
import { format } from 'date-fns';

import {
  Typography,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Divider,
  Card,
  CardContent,
  Grid, IconButton,
} from '@mui/material';
import ExistingCompanyForm from '../Registration/ExistingCompanyForm';
import NewCompanyForm from '../Registration/NewCompanyForm';
import SelfSponsoredForm from '../Registration/SelfSponsoredForm';
import ExistingSelfSponsoredForm from '../Registration/ExistingSelfSponsoredForm'
import { useParams } from 'react-router-dom'; // Import useParams to get batchid
import { LibraryBooks, Place, Group, AccessAlarm, AttachMoney, Info } from '@mui/icons-material'; // Import icons
import Header from '../../Component/Header';

const Companies = () => {
  const { batchid } = useParams(); // Get the batchid from the URL params
  const [registrationType, setRegistrationType] = useState('company-sponsored');
  const [clientType, setClientType] = useState('');
  const [courseData, setCourseData] = useState(null); // State to store course data
  const [loading, setLoading] = useState(true); // State for loading status
  const [error, setError] = useState(null); // State for error handling

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const response = await axios.get(`/wsq/batches/${batchid}`);
        setCourseData(response.data); // Set course data based on the batch
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCourseData();
  }, [batchid]);

  const handleRegistrationTypeChange = (event) => {
    setRegistrationType(event.target.value);
    setClientType('');
  };

  const handleClientTypeChange = (event) => {
    setClientType(event.target.value);
  };

  const renderCompanySponsoredForm = () => (
    <>
      <Divider sx={{ marginY: 2 }} />
      <FormControl component="fieldset" margin="normal">
        <Typography variant="subtitle1" gutterBottom>
          <strong>CLIENT TYPE:</strong>
        </Typography>
        <RadioGroup
          row
          aria-label="client-type"
          name="client-type"
          value={clientType}
          onChange={handleClientTypeChange}
        >
          <FormControlLabel
            value="existing-client"
            control={<Radio />}
            label="Existing Client Company"
          />
          <FormControlLabel
            value="new-client"
            control={<Radio />}
            label="New Client Company"
          />
        </RadioGroup>
      </FormControl>

      {/* Render Existing Client Company Form */}
      {clientType === 'existing-client' && courseData && <ExistingCompanyForm dataFrom="admin" fee={courseData.course.fee} />}
      {/* Render New Client Company Form */}
      {clientType === 'new-client' && courseData && <NewCompanyForm dataFrom="admin" fee={courseData.course.fee} />}
    </>
  );

  const renderSelfSponsoredForm = () => (
    <>
      <Divider sx={{ marginY: 2 }} />
      <FormControl component="fieldset" margin="normal">
        <Typography variant="subtitle1" gutterBottom>
          <strong>CLIENT TYPE:</strong>
        </Typography>
        <RadioGroup
          row
          aria-label="client-type"
          name="client-type"
          value={clientType}
          onChange={handleClientTypeChange}
        >
          <FormControlLabel
            value="Self_Sponsored"
            control={<Radio />}
            label="Existing Self Sponsored"
          />
          <FormControlLabel
            value="New_Self_Sponsored"
            control={<Radio />}
            label="New Self Sponsored"
          />
        </RadioGroup>
      </FormControl>

      {/* Render Existing Client Company Form */}
      {clientType === 'Self_Sponsored' && courseData && <ExistingSelfSponsoredForm fee={courseData.course.fee} />}

      {/* Render New Client Company Form */}
      {clientType === 'New_Self_Sponsored' && courseData && <SelfSponsoredForm fee={courseData.course.fee} />}
    </>
  );

  function generateCourseCode(courseName, courseId) {
    // Extract the first 4 characters of the course name for abbreviation
    const courseNameAbbreviation = courseName
      .split(' ') // Split by spaces to get parts of the course name
      .map(word => word.substring(0, 1).toUpperCase()) // Get the first letter of each word and capitalize
      .join(''); // Join the first letters to form the abbreviation

    // Add 'CHG' after abbreviation (as per the expected value)
    const fullCourseAbbreviation = `${courseNameAbbreviation}`;

    // Format the course ID to be 2 digits (e.g., 02 for 2)
    const formattedId = courseId.toString().padStart(2, '0');

    // Return the final code in the format 'DMENCHG-02'
    return `${fullCourseAbbreviation}-${formattedId}`;
  }


  return (
    <div>
      <Header />

      <Paper
        elevation={3}
        sx={{
          padding: 4,
          borderRadius: 2,
          border: '1px solid #ccc',
          maxWidth: '800px',
          margin: '20px auto',
          backgroundColor: '#f9f9f9',
        }}
      >
        <Typography variant="h4" textAlign={'center'} gutterBottom>
          <strong>Company Sponsored Registration</strong>
        </Typography>

        {/* Display loading or error message */}
        {loading && <Typography>Loading course data...</Typography>}
        {error && <Typography color="error">{error}</Typography>}

        {/* Display course data if available */}
        {courseData && (
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                📚 Course Information
              </Typography>
              <Divider />
              <br />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <LibraryBooks color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Name:</Typography>
                    <Typography variant="body2">{courseData.course.name}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <AttachMoney color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Fee:</Typography>
                    <Typography variant="body2">${courseData.course.fee}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Translate color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Language:</Typography>
                    <Typography variant="body2">{courseData.language}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Place color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Venue:</Typography>
                    <Typography variant="body2">
                      {courseData.venue.place}, Room {courseData.venue.roomno}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Group color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Intake Size:</Typography>
                    <Typography variant="body2">{courseData.intakeSize}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <VpnKey color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Batch ID:</Typography>
                    <Typography variant="body2">{generateCourseCode(courseData.course.name, courseData.id)}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {courseData.days && courseData.days.length > 0 && (
                <Box mt={3}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 'bold', color: 'primary.main' }}
                  >
                    Session Details
                  </Typography>
                  <Grid container spacing={2}>
                    {courseData.days.map((day, index) => (
                      <Grid item xs={12} key={index}>
                        <Box
                          sx={{
                            p: 1,
                            borderLeft: 1,
                            borderColor: 'primary.light',
                          }}
                        >

                          {/* Display Start Date on the left and End Date on the right */}
                          <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Day {index + 1}: {format(new Date(day.date), 'dd:MM:yyyy')}
                          </Typography>
                          {/* {day.session.map((session, i) => (
              <Box key={i} display="flex" alignItems="center" sx={{ mb: 1 }}>
                <AccessAlarm color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  <strong>Session {i + 1}</strong> - {session.startTime} to {session.endTime}
                </Typography>
              </Box>
            ))} */}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}



            </CardContent>
          </Card>

        )}

        <FormControl component="fieldset" margin="normal">
          <Typography variant="subtitle1" gutterBottom>
            <strong>REGISTRATION TYPE:</strong>
          </Typography>
          <RadioGroup
            row
            aria-label="registration-type"
            name="registration-type"
            value={registrationType}
            onChange={handleRegistrationTypeChange}
          >
            <FormControlLabel
              value="company-sponsored"
              control={<Radio />}
              label="Company Sponsored"
            />
            <FormControlLabel
              value="self-sponsored"
              control={<Radio />}
              label="Self Sponsored"
            />
          </RadioGroup>
        </FormControl>

        {/* Render form based on registration type */}
        {registrationType === 'company-sponsored' && renderCompanySponsoredForm()}
        {registrationType === 'self-sponsored' && renderSelfSponsoredForm()}

      </Paper>
    </div>
  );
};

export default Companies;
